import * as classnames from 'classnames'
import * as React from 'react'
import { CardElement, Elements, injectStripe, StripeProvider } from 'react-stripe-elements'

import { PaymentEventDateType, PaymentRefererType } from '../../../../commons/PaymentFormStatus'
import { Plan } from '../../../../commons/Plan'
import { buildOtherUrl } from '../../../../commons/utils'
import Button from '../../atoms/Button/Button'
import RadioButton from '../../atoms/RadioButton/RadioButton'
import FormLayout from '../../molecules/FormLayout/FormLayout'
import LimitTextField from '../../molecules/LimitTextField/LimitTextField'
import * as styles from './PaymentForm.css'

interface PaymentFormProps {
  stripe: any
  firstName: string
  familyName: string
  completeInputForm: boolean
  plan: Plan
  selectedDateOption: string
  eventDate: string
  selectedRefererOption: string
  otherReferer: string
  formErrorMessage: any
  handleChangeForm: (data: any) => void
  completeCreateToken: (data: any) => void
}

interface PaymentFormContentProps {
  stripe: any
  firstName: string
  familyName: string
  plan: Plan
  completeInputForm: boolean
  selectedDateOption: string
  eventDate: string
  formErrorMessage: any
  selectedRefererOption: string
  otherReferer: string
  handleChangeForm: (data: any) => void
  completeCreateToken: (data: any) => void
}

interface PaymentFormContentState {
  isFocusStripeForm: boolean
  isLoading: boolean
  errorMessage: string | null
}

class PaymentFormContent extends React.Component<PaymentFormContentProps, PaymentFormContentState> {
  constructor(params: any) {
    super(params)
    this.state = { isFocusStripeForm: false, isLoading: false, errorMessage: null }
  }

  public render() {
    const {
      stripe,
      firstName,
      familyName,
      plan,
      selectedDateOption,
      eventDate,
      selectedRefererOption,
      otherReferer,
      handleChangeForm,
      completeCreateToken,
      completeInputForm,
      formErrorMessage
    } = this.props
    const createStripeToken = () => {
      this.setState({ isLoading: true })
      stripe
        .createToken()
        .then((result: any) => {
          // 402でもここに入る仕様
          if (result.error) {
            if (result.error.decline_code === 'card_not_supported') {
              this.setState({ errorMessage: '申し訳ありません。こちらのカードには現在対応しておりません。' })
            } else {
              this.setState({ errorMessage: result.error.message })
            }
            return
          }
          completeCreateToken(result)
        })
        .finally(() => {
          this.setState({ isLoading: false })
        })
    }
    return (
      <div>
        <div className={styles.plan}>
          <FormLayout title="プラン">
            <p className={styles.name}>{Plan.info(plan).name}</p>
          </FormLayout>
        </div>
        <div className={styles.name}>
          <div className={styles.familyName}>
            <LimitTextField
              title="姓"
              showLimitCount={false}
              text={familyName}
              handleChange={(e: any) => handleChangeForm({ familyName: e.targetTitle })}
            />
          </div>
          <div className={styles.firstName}>
            <LimitTextField
              title="名"
              showLimitCount={false}
              text={firstName}
              handleChange={(e: any) => handleChangeForm({ firstName: e.targetTitle })}
            />
          </div>
        </div>
        <div className={classnames(styles.cardNumber, this.state.isFocusStripeForm ? styles.selected : null)}>
          <FormLayout title="クレジットカード情報">
            <CardElement
              style={{ base: { color: '#3F4B40', '::placeholder': { color: '#d4d6d8' } } }}
              onBlur={() => this.setState({ isFocusStripeForm: false })}
              onFocus={() => this.setState({ isFocusStripeForm: true })}
              onChange={e => handleChangeForm({ completeCreditCard: e.complete })}
            />
          </FormLayout>
          {this.state.errorMessage ? <p className={styles.cardError}>{this.state.errorMessage}</p> : null}
        </div>
        <div>
          <FormLayout title="ご利用予定日">
            <div className={styles.dateFormContainer}>
              <div className={styles.radioButton}>
                <RadioButton
                  value={PaymentEventDateType.UNKNOWN}
                  selectedOption={selectedDateOption}
                  handleChange={(e: any) => handleChangeForm({ selectedDateOption: String(e) })}
                  text={'未定'}
                />
              </div>
              <div className={styles.radioButtonWithText}>
                <RadioButton
                  value={PaymentEventDateType.DETERMINED}
                  selectedOption={selectedDateOption}
                  handleChange={(e: any) => handleChangeForm({ selectedDateOption: String(e) })}
                />
                <LimitTextField
                  title={null}
                  showLimitCount={false}
                  text={eventDate}
                  placeholder={'yyyy/mm/dd'}
                  handleChange={(e: any) => handleChangeForm({ eventDate: e.targetTitle })}
                  disabled={selectedDateOption === PaymentEventDateType.DETERMINED ? false : true}
                />
                <p>に開催</p>
              </div>
            </div>
          </FormLayout>
          {selectedDateOption === PaymentEventDateType.DETERMINED && 'eventDate' in formErrorMessage ? (
            <p className={styles.formError}>{formErrorMessage.eventDate}</p>
          ) : null}
        </div>
        <div>
          <FormLayout title="Ennoshitaを知ったきっかけ(任意)">
            <div className={styles.refererFormContainer}>
              <div className={styles.radioButton}>
                <RadioButton
                  value={PaymentRefererType.WEB_SEARCH}
                  selectedOption={selectedRefererOption}
                  handleChange={(e: any) => handleChangeForm({ selectedRefererOption: String(e) })}
                  text={'WEB検索'}
                />
              </div>
              <div className={styles.radioButton}>
                <RadioButton
                  value={PaymentRefererType.SNS}
                  selectedOption={selectedRefererOption}
                  handleChange={(e: any) => handleChangeForm({ selectedRefererOption: String(e) })}
                  text={'SNSから'}
                />
              </div>
              <div className={styles.radioButton}>
                <RadioButton
                  value={PaymentRefererType.FRIEND}
                  selectedOption={selectedRefererOption}
                  handleChange={(e: any) => handleChangeForm({ selectedRefererOption: String(e) })}
                  text={'知人から'}
                />
              </div>
              <div className={styles.radioButtonWithText}>
                <RadioButton
                  value={PaymentRefererType.OTHER}
                  selectedOption={selectedRefererOption}
                  handleChange={(e: any) => handleChangeForm({ selectedRefererOption: String(e) })}
                  text={undefined}
                />
                <LimitTextField
                  title={null}
                  showLimitCount={false}
                  text={otherReferer}
                  placeholder={'その他'}
                  handleChange={(e: any) => handleChangeForm({ otherReferer: e.targetTitle })}
                  disabled={selectedRefererOption === PaymentRefererType.OTHER ? false : true}
                />
              </div>
            </div>
          </FormLayout>
          {selectedDateOption === PaymentRefererType.OTHER && 'referer' in formErrorMessage ? (
            <p className={styles.formError}>{formErrorMessage.referer}</p>
          ) : null}
        </div>
        <div className={styles.confirmButtonPart}>
          <p className={styles.agreementText}>
            <a className={styles.agreementLink} href={buildOtherUrl('/privacy')} target="_blank">
              プライバシーポリシー
            </a>
            、
            <a className={styles.agreementLink} href={buildOtherUrl('/terms')} target="_blank">
              利用規約
            </a>
            に同意して
          </p>
          <div className={styles.confirmButton}>
            <Button
              handleClick={() => createStripeToken()}
              isLoading={this.state.isLoading}
              isEnabled={completeInputForm}
            >
              <p>確認画面へ</p>
            </Button>
          </div>
        </div>
      </div>
    )
  }
}

const InjectedStripeForm = injectStripe(PaymentFormContent)

const PaymentForm: React.FunctionComponent<PaymentFormProps> = ({
  stripe,
  firstName,
  familyName,
  plan,
  selectedDateOption,
  eventDate,
  selectedRefererOption,
  otherReferer,
  handleChangeForm,
  completeCreateToken,
  completeInputForm,
  formErrorMessage
}) => {
  return (
    <StripeProvider stripe={stripe}>
      <Elements>
        <InjectedStripeForm
          firstName={firstName}
          familyName={familyName}
          plan={plan}
          handleChangeForm={handleChangeForm}
          stripe={stripe}
          selectedDateOption={selectedDateOption}
          eventDate={eventDate}
          selectedRefererOption={selectedRefererOption}
          otherReferer={otherReferer}
          completeCreateToken={completeCreateToken}
          completeInputForm={completeInputForm}
          formErrorMessage={formErrorMessage}
        />
      </Elements>
    </StripeProvider>
  )
}

export default PaymentForm
